"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, GraduationCap, Cpu, LifeBuoy, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  title: string;
  tag: string;
  iconName: "wrench" | "training" | "integration" | "support";
  summary: string;
  detail1: string;
  detail2: string;
  detail3: string;
  linkUrl: string;
  actionText: string;
}
const servicesData: ServiceItem[] = [{
  id: "srv-1",
  title: "Calibration & Field Maintenance",
  tag: "Preventive Care",
  iconName: "wrench",
  summary: "Routine verification, ISO-aligned calibrations, and rapid on-site troubleshooting for analytical instruments in regulated labs.",
  detail1: "Manufacturer-traceable calibration standards",
  detail2: "Annual and semi-annual service contracts",
  detail3: "Complete IQ/OQ/PQ documentation packs",
  linkUrl: "/PreventiveMaintenanceAndRepairsPage",
  actionText: "Maintenance details"
}, {
  id: "srv-2",
  title: "Operator Training & Certification",
  tag: "Competence & Compliance",
  iconName: "training",
  summary: "Structured practical instruction for laboratory analysts, technicians, and QA personnel on routine operation and data workflows.",
  detail1: "On-site and remote classroom options",
  detail2: "SOP-aligned handling and test execution",
  detail3: "Individual assessment & certification logs",
  linkUrl: "/TrainingApplicationSupportPage",
  actionText: "View training modules"
}, {
  id: "srv-3",
  title: "Integration & Deployment Consulting",
  tag: "Systems & PAT",
  iconName: "integration",
  summary: "Engineering guidance for integrating Process Analytical Technology (PAT) and spectroscopy equipment into live production lines.",
  detail1: "Interface design with manufacturing SCADA/MES",
  detail2: "Sampling point selection & optical probe fitting",
  detail3: "Hazardous area (ATEX) compliance reviews",
  linkUrl: "/ApplicationConsultationPage",
  actionText: "Consulting scope"
}, {
  id: "srv-4",
  title: "Technical Support & Spares",
  tag: "Lifecycle Supply",
  iconName: "support",
  summary: "Dedicated helpdesk triage, firmware patch management, and prompt dispatch of OEM replacement consumables and optics.",
  detail1: "Scheduled service response",
  detail2: "Critical spare parts stock management",
  detail3: "Direct engineer-to-engineer remote diagnosis",
  linkUrl: "/SupportRequestPage",
  actionText: "Submit support request"
}];
function renderServiceIcon(name: ServiceItem["iconName"]) {
  switch (name) {
    case "wrench":
      return <Wrench className="size-6 text-primary" />;
    case "training":
      return <GraduationCap className="size-6 text-primary" />;
    case "integration":
      return <Cpu className="size-6 text-primary" />;
    case "support":
      return <LifeBuoy className="size-6 text-primary" />;
  }
}
export default function ServicesGrid() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0.1 : 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section id="servicesgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4">
            Lifecycle & Engineering Services
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
            Technical services built for regulated laboratories and plants.
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            From initial instrument deployment to routine maintenance and operator training,
            we provide factual engineering support to protect uptime and data integrity.
          </p>
        </div>

        {/* 2x2 Services Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {servicesData.map((service, i) => <motion.div key={service.id} data-index={i} variants={itemVariants}>
              <Card className="h-full border border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200 flex flex-col justify-between bg-card text-card-foreground">
                <div>
                  <CardHeader className="space-y-4 pb-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="size-12 rounded-lg bg-secondary flex items-center justify-center border border-border">
                        {renderServiceIcon(service.iconName)}
                      </div>
                      <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                        {service.tag}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl md:text-2xl font-semibold text-card-foreground">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm md:text-base text-muted-foreground leading-relaxed">
                      {service.summary}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2">
                    <div className="border-t border-border pt-4 space-y-2.5">
                      <div className="flex items-start gap-2.5 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{service.detail1}</span>
                      </div>
                      <div className="flex items-start gap-2.5 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{service.detail2}</span>
                      </div>
                      <div className="flex items-start gap-2.5 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{service.detail3}</span>
                      </div>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-4 border-t border-border mt-6">
                  <Button asChild variant="outline" className="w-full justify-between group">
                    <Link to={service.linkUrl}>
                      <span>{service.actionText}</span>
                      <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Optional Secondary Support Banner */}
        <div className="mt-12 p-6 md:p-8 rounded-xl bg-secondary border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-secondary-foreground font-default">
              Need custom service agreements or emergency engineer dispatch?
            </h3>
            <p className="text-sm text-muted-foreground">
              Speak directly with our technical support team in Ireland and the UK.
            </p>
          </div>
          <Button asChild variant="default" className="shrink-0">
            <Link to="/ContactPage">
              <span>Contact Engineering</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}